import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  getPosConnectionsFromServer,
  updatePosConnectionServer,
} from "../businesses/businessesApi";
import {
  ConnectToPosResponse,
  ManualOrderItem,
  PosConnectionStatus,
  PosCredentials,
  PosItem,
  PosOrder,
  PosProviderType,
  PosTable,
} from "../../interfaces/posSimulator.interface";
import {
  connectToPos,
  createOrder,
  fetchItems,
  fetchOrder,
  fetchTables,
  payOrder,
} from "./PosSimulatorApi";
import { isSamePosItem } from "./utils/posItemIdentity";

export interface ExistingPosConnection {
  _id: string;
  posProvider: PosProviderType;
  status: "CONNECTED" | "PENDING" | "BAD_CREDENTIALS" | "DELETED";
  createdAt: string;
  updatedAt: string;
}

export interface PosSimulatorState {
  businessId: string;
  existingConnections: ExistingPosConnection[];
  connectionsLoading: boolean;
  posConnectionId: string | null;
  connectionStatus: "idle" | "connecting" | "connected" | "error";
  connectionLabel: PosConnectionStatus | null;
  tables: PosTable[];
  tablesLoading: boolean;
  selectedTable: PosTable | null;
  order: PosOrder | null;
  orderLoading: boolean;
  basket: ManualOrderItem[];
  createOrderLoading: boolean;
  payOrderLoading: boolean;
  posItems: PosItem[];
  posItemsLoading: boolean;
  error: string | null;
}

const initialState: PosSimulatorState = {
  businessId: "",
  existingConnections: [],
  connectionsLoading: false,
  posConnectionId: null,
  connectionStatus: "idle",
  connectionLabel: null,
  tables: [],
  tablesLoading: false,
  selectedTable: null,
  order: null,
  orderLoading: false,
  basket: [],
  createOrderLoading: false,
  payOrderLoading: false,
  posItems: [],
  posItemsLoading: false,
  error: null,
};

export const loadPosConnections = createAsyncThunk<
  ExistingPosConnection[],
  string
>("posSimulator/loadPosConnections", async (businessId) => {
  const res = await getPosConnectionsFromServer(businessId);
  return res.connections as ExistingPosConnection[];
});

export const submitConnect = createAsyncThunk<
  ConnectToPosResponse,
  {
    businessId: string;
    posProvider: PosProviderType;
    credentials?: PosCredentials;
  }
>("posSimulator/connect", async ({ businessId, posProvider, credentials }) => {
  return connectToPos(businessId, posProvider, credentials);
});

export const submitUpdateConnection = createAsyncThunk<
  ConnectToPosResponse,
  {
    posConnectionId: string;
    businessId: string;
    credentials: Record<string, string>;
  }
>(
  "posSimulator/updateConnection",
  async ({ posConnectionId, businessId, credentials }) => {
    return updatePosConnectionServer({
      posConnectionId,
      businessId,
      credentials,
    });
  }
);

export const loadTables = createAsyncThunk<PosTable[], string>(
  "posSimulator/loadTables",
  async (businessId) => fetchTables(businessId)
);

export const loadOrder = createAsyncThunk<
  PosOrder | null,
  {
    businessId: string;
    posTableId: string;
    posTableMetadata: Record<string, unknown>;
  }
>(
  "posSimulator/loadOrder",
  async ({ businessId, posTableId, posTableMetadata }) => {
    return fetchOrder(businessId, posTableId, posTableMetadata);
  }
);

export const submitCreateOrder = createAsyncThunk<
  { posOrderId: string },
  {
    businessId: string;
    posTableId: string;
    posTableMetadata: Record<string, unknown>;
    items: ManualOrderItem[];
    depositAmount?: number;
  }
>(
  "posSimulator/createOrder",
  async ({
    businessId,
    posTableId,
    posTableMetadata,
    items,
    depositAmount,
  }) => {
    return createOrder(
      businessId,
      posTableId,
      posTableMetadata,
      items,
      depositAmount
    );
  }
);

export const loadPosItems = createAsyncThunk<PosItem[], string>(
  "posSimulator/loadPosItems",
  async (businessId) => fetchItems(businessId)
);

export const submitPayOrder = createAsyncThunk<
  void,
  {
    businessId: string;
    posOrderId: string;
    posTableMetadata: Record<string, unknown>;
    amount: number;
  }
>(
  "posSimulator/payOrder",
  async ({ businessId, posOrderId, posTableMetadata, amount }) => {
    await payOrder(businessId, posOrderId, posTableMetadata, amount);
  }
);

const posSimulatorSlice = createSlice({
  name: "posSimulator",
  initialState,
  reducers: {
    setBusinessId(state, action: PayloadAction<string>) {
      state.businessId = action.payload;
    },
    selectTable(state, action: PayloadAction<PosTable>) {
      state.selectedTable = action.payload;
      state.order = null;
      state.basket = [];
    },
    clearSelectedTable(state) {
      state.selectedTable = null;
      state.order = null;
      state.basket = [];
    },
    addBasketItem(state, action: PayloadAction<ManualOrderItem>) {
      const incoming = action.payload;
      const existing = state.basket.find((i) => isSamePosItem(i, incoming));
      if (existing) {
        existing.quantity += incoming.quantity;
      } else {
        state.basket.push(incoming);
      }
    },
    updateBasketItem(state, action: PayloadAction<ManualOrderItem>) {
      const idx = state.basket.findIndex(
        (i) => i._localId === action.payload._localId
      );
      if (idx !== -1) state.basket[idx] = action.payload;
    },
    removeBasketItem(state, action: PayloadAction<string>) {
      state.basket = state.basket.filter((i) => i._localId !== action.payload);
    },
    clearBasket(state) {
      state.basket = [];
    },
    clearOrder(state) {
      state.order = null;
    },
    setPosItems(state, action: PayloadAction<PosItem[]>) {
      state.posItems = action.payload;
      state.posItemsLoading = false;
    },
    resetPosSimulator: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // loadPosConnections
      .addCase(loadPosConnections.pending, (state) => {
        state.connectionsLoading = true;
        state.existingConnections = [];
        state.error = null;
      })
      .addCase(loadPosConnections.fulfilled, (state, action) => {
        state.connectionsLoading = false;
        state.existingConnections = action.payload;
      })
      .addCase(loadPosConnections.rejected, (state, action) => {
        state.connectionsLoading = false;
        state.error = action.error.message ?? "Failed to load connections";
      })

      // submitConnect
      .addCase(submitConnect.pending, (state) => {
        state.connectionStatus = "connecting";
        state.error = null;
      })
      .addCase(submitConnect.fulfilled, (state, action) => {
        state.connectionStatus = "connected";
        state.connectionLabel = action.payload.status;
        state.posConnectionId = action.payload.posConnectionId;
      })
      .addCase(submitConnect.rejected, (state, action) => {
        state.connectionStatus = "error";
        state.error = action.error.message ?? "Connection failed";
      })

      // submitUpdateConnection
      .addCase(submitUpdateConnection.pending, (state) => {
        state.connectionStatus = "connecting";
        state.error = null;
      })
      .addCase(submitUpdateConnection.fulfilled, (state, action) => {
        state.connectionStatus = "connected";
        state.connectionLabel = action.payload.status;
        state.posConnectionId = action.payload.posConnectionId;
      })
      .addCase(submitUpdateConnection.rejected, (state, action) => {
        state.connectionStatus = "error";
        state.error = action.error.message ?? "Update failed";
      })

      // loadTables
      .addCase(loadTables.pending, (state) => {
        state.tablesLoading = true;
        state.tables = [];
        state.selectedTable = null;
        state.order = null;
        state.error = null;
      })
      .addCase(loadTables.fulfilled, (state, action) => {
        state.tablesLoading = false;
        state.tables = action.payload;
      })
      .addCase(loadTables.rejected, (state, action) => {
        state.tablesLoading = false;
        state.error = action.error.message ?? "Failed to load tables";
      })

      // loadOrder
      .addCase(loadOrder.pending, (state) => {
        state.orderLoading = true;
        state.error = null;
      })
      .addCase(loadOrder.fulfilled, (state, action) => {
        state.orderLoading = false;
        state.order = action.payload;
      })
      .addCase(loadOrder.rejected, (state, action) => {
        state.orderLoading = false;
        state.error = action.error.message ?? "Failed to fetch order";
      })

      // submitCreateOrder
      .addCase(submitCreateOrder.pending, (state) => {
        state.createOrderLoading = true;
        state.error = null;
      })
      .addCase(submitCreateOrder.fulfilled, (state) => {
        state.createOrderLoading = false;
        state.basket = [];
      })
      .addCase(submitCreateOrder.rejected, (state, action) => {
        state.createOrderLoading = false;
        state.error = action.error.message ?? "Failed to create order";
      })

      // loadPosItems
      .addCase(loadPosItems.pending, (state) => {
        state.posItemsLoading = true;
        state.error = null;
      })
      .addCase(loadPosItems.fulfilled, (state, action) => {
        state.posItemsLoading = false;
        state.posItems = action.payload;
      })
      .addCase(loadPosItems.rejected, (state, action) => {
        state.posItemsLoading = false;
        state.error = action.error.message ?? "Failed to load items";
      })

      // submitPayOrder
      .addCase(submitPayOrder.pending, (state) => {
        state.payOrderLoading = true;
        state.error = null;
      })
      .addCase(submitPayOrder.fulfilled, (state) => {
        state.payOrderLoading = false;
        state.order = null;
      })
      .addCase(submitPayOrder.rejected, (state, action) => {
        state.payOrderLoading = false;
        state.error = action.error.message ?? "Failed to pay order";
      });
  },
});

export const {
  setBusinessId,
  selectTable,
  clearSelectedTable,
  addBasketItem,
  updateBasketItem,
  removeBasketItem,
  clearBasket,
  clearOrder,
  setPosItems,
  resetPosSimulator,
} = posSimulatorSlice.actions;

export const selectBusinessId = (state: RootState) =>
  state.posSimulator.businessId;
export const selectExistingConnections = (state: RootState) =>
  state.posSimulator.existingConnections;
export const selectActiveProvider = (
  state: RootState
): PosProviderType | null => {
  const conns = state.posSimulator.existingConnections;
  const connected = conns.find((c) => c.status === "CONNECTED");
  return connected?.posProvider ?? conns[0]?.posProvider ?? null;
};
export const selectConnectionsLoading = (state: RootState) =>
  state.posSimulator.connectionsLoading;
export const selectPosConnectionId = (state: RootState) =>
  state.posSimulator.posConnectionId;
export const selectConnectionStatus = (state: RootState) =>
  state.posSimulator.connectionStatus;
export const selectConnectionLabel = (state: RootState) =>
  state.posSimulator.connectionLabel;
export const selectPosTables = (state: RootState) => state.posSimulator.tables;
export const selectTablesLoading = (state: RootState) =>
  state.posSimulator.tablesLoading;
export const selectSelectedTable = (state: RootState) =>
  state.posSimulator.selectedTable;
export const selectPosOrder = (state: RootState) => state.posSimulator.order;
export const selectOrderLoading = (state: RootState) =>
  state.posSimulator.orderLoading;
export const selectBasket = (state: RootState) => state.posSimulator.basket;
export const selectCreateOrderLoading = (state: RootState) =>
  state.posSimulator.createOrderLoading;
export const selectPayOrderLoading = (state: RootState) =>
  state.posSimulator.payOrderLoading;
export const selectPosError = (state: RootState) => state.posSimulator.error;
export const selectPosItems = (state: RootState) => state.posSimulator.posItems;
export const selectPosItemsLoading = (state: RootState) =>
  state.posSimulator.posItemsLoading;

export default posSimulatorSlice.reducer;
